import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, RefreshCw, Trash2, ShieldCheck } from "lucide-react";

/** Phase O.Z — Production Master Reset (Admin-only).
 *
 *  Two-step UX:
 *    1. Dry-run: shows the per-collection delete counts. Safe. No writes.
 *    2. Apply:   requires typing the exact confirm string AND the operator's
 *                email must be in the backend's hard-coded allow-list.
 *
 *  All destructive intent is plain-text and auditable. The backend writes
 *  an admin_activity row BEFORE deletion, so even a mid-request crash is
 *  traceable.
 */
const REQUIRED_CONFIRM = "I-UNDERSTAND-THIS-DELETES-PROD-DATA";
const DEFAULT_KEEP = "mrunalshukla79@gmail.com,admin@aivideoos.com";
const DEFAULT_DELETE = "demo@aivideoos.com";

export default function ProdResetTab() {
    const { user } = useAuth();
    const [keepEmails, setKeepEmails] = useState(DEFAULT_KEEP);
    const [deleteEmails, setDeleteEmails] = useState(DEFAULT_DELETE);
    const [confirm, setConfirm] = useState("");
    const [dryReport, setDryReport] = useState(null);
    const [applyResult, setApplyResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const allowedToApply =
        user?.email === "mrunalshukla79@gmail.com"
        || user?.email === "admin@aivideoos.com";

    async function runDryRun() {
        if (busy) return;
        setBusy(true);
        setApplyResult(null);
        try {
            const { data } = await api.post("/admin/prod-reset/dry-run", {
                keep_admin_emails: keepEmails.split(",").map((s) => s.trim()).filter(Boolean),
                delete_emails:    deleteEmails.split(",").map((s) => s.trim()).filter(Boolean),
            });
            setDryReport(data);
            toast.success(`Dry-run complete — ${data.total_rows_to_delete} rows would be deleted.`);
        } catch (e) {
            toast.error(`Dry-run failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    }

    async function runApply() {
        if (busy) return;
        if (confirm !== REQUIRED_CONFIRM) {
            toast.error(`Type the exact confirm string: ${REQUIRED_CONFIRM}`);
            return;
        }
        if (!window.confirm(
            "Final check: This will DELETE all non-admin production data. " +
            "Continue?"
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/prod-reset/apply", {
                confirm,
                keep_admin_emails: keepEmails.split(",").map((s) => s.trim()).filter(Boolean),
                delete_emails:    deleteEmails.split(",").map((s) => s.trim()).filter(Boolean),
            });
            setApplyResult(data);
            if (data.ok) {
                toast.success(`Reset complete. ${data.admins_remaining} admin(s) preserved.`);
            } else {
                toast.error("Reset finished with integrity warnings — review the report.");
            }
        } catch (e) {
            toast.error(`Apply failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-6" data-testid="prod-reset-tab">
            <div className="border border-red-500/40 bg-red-500/5 p-4 rounded-sm">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-red-300">DESTRUCTIVE OPERATION</div>
                        <div className="text-sm text-zinc-300 mt-1">
                            This wipes all non-admin operational data from the production
                            database. Admin accounts in <span className="font-mono">KEEP_ADMIN_EMAILS</span> are preserved.
                            ALWAYS run dry-run first.
                        </div>
                    </div>
                </div>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <label className="block">
                    <div className="label-micro text-zinc-500 mb-1">Keep admin emails (comma-separated)</div>
                    <input
                        type="text"
                        value={keepEmails}
                        onChange={(e) => setKeepEmails(e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono"
                        data-testid="prod-reset-keep-emails"
                    />
                </label>
                <label className="block">
                    <div className="label-micro text-zinc-500 mb-1">Delete emails (comma-separated)</div>
                    <input
                        type="text"
                        value={deleteEmails}
                        onChange={(e) => setDeleteEmails(e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono"
                        data-testid="prod-reset-delete-emails"
                    />
                </label>
            </div>

            <button
                type="button"
                onClick={runDryRun}
                disabled={busy}
                data-testid="prod-reset-dryrun-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/15 border border-blue-500/40 text-blue-300 rounded-sm hover:bg-blue-500/25 disabled:opacity-50 transition-colors"
            >
                <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
                Run Dry-Run (safe)
            </button>

            {dryReport && (
                <div className="border border-zinc-800 rounded-sm p-4 bg-[#0a0a0a]" data-testid="prod-reset-dry-report">
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-medium">Dry-Run Report</div>
                        <div className="text-xs text-zinc-500">
                            Total deletion: <span className="text-red-400 font-mono">{dryReport.total_rows_to_delete}</span> rows
                        </div>
                    </div>
                    <div className="text-xs text-zinc-400 mb-3">
                        Admin emails preserved:&nbsp;
                        <span className="font-mono text-emerald-300">{(dryReport.admin_emails_preserved || []).join(", ") || "(none)"}</span>
                    </div>
                    {dryReport.warning_if_demo_seed_active && (
                        <div className="text-xs text-amber-300 mb-3 border border-amber-500/30 bg-amber-500/5 p-2 rounded-sm">
                            ⚠ {dryReport.warning_if_demo_seed_active}
                        </div>
                    )}
                    <table className="w-full text-xs font-mono">
                        <thead className="text-zinc-500">
                            <tr>
                                <th className="text-left py-1">Collection</th>
                                <th className="text-left">Action</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">Keep</th>
                                <th className="text-right">Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(dryReport.report).sort(([a], [b]) => a.localeCompare(b)).map(([col, r]) => (
                                <tr key={col} className="border-t border-zinc-900">
                                    <td className="py-1">{col}</td>
                                    <td className="text-zinc-500">{r.action}</td>
                                    <td className="text-right">{r.total}</td>
                                    <td className="text-right text-emerald-300">{r.keep}</td>
                                    <td className="text-right text-red-400">{r.delete}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Apply block — only after a dry-run + allowed email */}
            {dryReport && allowedToApply && (
                <div className="border border-red-500/40 rounded-sm p-4 bg-red-500/5">
                    <div className="font-medium text-red-300 flex items-center gap-2 mb-3">
                        <Trash2 className="w-4 h-4" /> Apply (destructive)
                    </div>
                    <div className="text-xs text-zinc-400 mb-2">
                        Type exactly: <span className="font-mono text-red-300">{REQUIRED_CONFIRM}</span>
                    </div>
                    <input
                        type="text"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder={REQUIRED_CONFIRM}
                        className="w-full bg-[#0a0a0a] border border-red-500/40 rounded-sm px-3 py-2 text-sm font-mono mb-3"
                        data-testid="prod-reset-confirm-input"
                    />
                    <button
                        type="button"
                        onClick={runApply}
                        disabled={busy || confirm !== REQUIRED_CONFIRM}
                        data-testid="prod-reset-apply-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/60 text-red-200 rounded-sm hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <Trash2 className="w-4 h-4" /> Apply (irreversible)
                    </button>
                </div>
            )}

            {dryReport && !allowedToApply && (
                <div className="border border-zinc-800 rounded-sm p-4 bg-[#0a0a0a] text-sm text-zinc-400">
                    Your account ({user?.email}) is not in the apply allow-list.
                    Only <span className="font-mono">mrunalshukla79@gmail.com</span> or <span className="font-mono">admin@aivideoos.com</span> can invoke apply.
                </div>
            )}

            {applyResult && (
                <div className="border border-emerald-500/40 rounded-sm p-4 bg-emerald-500/5"
                     data-testid="prod-reset-apply-result">
                    <div className="flex items-center gap-2 font-medium text-emerald-300 mb-2">
                        <ShieldCheck className="w-4 h-4" />
                        Apply complete · integrity_ok={String(applyResult.ok)}
                    </div>
                    <div className="text-xs text-zinc-300 font-mono">
                        Admins remaining: {applyResult.admins_remaining} · Legacy [object Object] rows: {applyResult.legacy_object_object_rows} · Kept: {(applyResult.kept_admin_emails || []).join(", ")}
                    </div>
                </div>
            )}
        </div>
    );
}
